import Commons from "oc-commons/Commons";
import Deckel from "./Deckel";
import Rueckwand from "./Rueckwand";
import Element from "./Element";
import Sockel from "./Sockel";
import Schrankboden from "./Schrankboden";
import Brett from "./Brett";
import { v3 } from "oc-threejs/short3";
import Slopings from "./Slopings";
import Maths3 from "oc-threejs/Maths3";
import SchrankO3 from "../object3d/Schrank";
import FullHeightDragable from "./FullHeightDragable";
import Helper from "../model/Helper";
import Schwebetuer from "./Schwebetuer";
import Maths from "oc-commons/Maths";
import { SchrankSettingsMixin } from "../model/SchrankSettingsMixing";
import UiSphere from "./UiSphere";
import Scenes from "oc-threejs/Scenes";
import { SchrankConnector } from "./SchrankConnector";

/**
 * Alle Koordinaten des Schraenke sind lokale Koordinaten. Der Nullpunkt ist links unten hinten,
 * der Punkt rechts oben vorne entspricht der Size des Schraenke.
 */
export default class Schrank {
    // DO NOT CHANGE
    className = "Schrank";
    // persistent (UserLayer) ------------------------------------------
    angleSide = "none";
    artikelnummer = "Schrank";
    // Falls dieser Schrank aus einer Inspiration entstanden ist..
    createdFromInspiration = null;

    // transient (CompiledLayer) und cached -----------------------------
    size = v3();
    // sizeTop = x: Breite Oben, y: Kniestock, z: Tiefe oben
    sizeTop = v3();
    elements = [];
    product;
    object3d;
    schraenke3d;
    innerWalls = [];
    outerWalls = {
        left: null,
        right: null,
    };
    deckel;
    rueckwand;
    schrankboden;
    sockel;
    schwebetuer;
    // Diese Variablen sind für die Nachbar Logik notwendig.
    neighbors = {
        left: null,
        right: null,
        top: null,
        bottom: null,
    };
    uiSpheres = [];
    possibleNeighborCombinations = null;

    // Eine Kameraposition die immer diesen Schrank perfekt im Blick hat.
    cameraPosition = null;
    // eine Liste von Intervallen bezogen auf die Verdeckung der Öffnungsseite. Beispiel: [[0,80]] bedeutet
    // dass die Öffnungsseite des Schrankes verdeckt ist, und zwar von der linken vorderen Seite aus gezählt.
    // [0,80] meint also eine Verdeckung von insgesamt 80 cm der linken Seite.
    verdeckungsIntervale = [];
    // position, quaternion und matrix sind im World-Koordinatensystem!
    position;
    quaternion;
    _matrix;
    _inverseMatrix;
    _matrixNeedsUpdate = true;
    slopings;
    slopeX = 0;
    // die Boxen sind im lokalen Koordinatensystem des Schranks
    box;
    elementsBox;
    corpusBox;
    corpusBoxWithFeet;
    sizeZisInnenmass = false;
    hasFronts = false;
    hasInterieurDoors = false;

    passleiste = {
        senkrechteLinks: false,
        senkrechteRechts: false,
        schraege: false,
        oben: false,
    };

    withRueckwand;
    withDeckel;
    withExzenterverbindung;

    constructor(arrangement, modell) {
        this.initCoordinateSystem();
        this.schrankboden = new Schrankboden(this);
        this.deckel = new Deckel(this);
        this.rueckwand = new Rueckwand(this);
        this.schwebetuer = new Schwebetuer(this);
        this.outerWalls.left = new Brett(this, "Aussenwand", "yz");
        this.outerWalls.right = new Brett(this, "Aussenwand", "yz");
        this.offset = 0;
        this.passleiste = {
            senkrechteLinks: false,
            senkrechteRechts: false,
            schraege: false,
            oben: false
        };
        this.modell = modell;
        this.sockelOption = "ohne";
        this.sockelHoehe = -1;
        this.checkModell();
    }

    // ----- JSON -----

    toJSON() {
        return {
            className: this.className,
            angleSide: this.angleSide,
            size: this.size,
            sizeTop: this.sizeTop,
            sizeOuter: this.sizeOuter,
            artikelnummer: this.artikelnummer,
            InternerName: this.product.InternerName,
            elements: this.elements.map((e) => e.toJSON()),
            boden: this.schrankboden.toJSON(),
            deckel: this.deckel.exists ? this.deckel.boeden?.map((b) => b.toJSON()) : null,
            innenwaende: this.innerWalls.map((w) => w.toJSON()),
            aussenwaende: Object.keys(this.outerWalls).map((key) => (this.outerWalls[key] ? this.outerWalls[key].toJSON() : null)),
            rueckwaende: this.rueckwand.exists ? this.rueckwand.walls?.map((w) => w.toJSON()) : null,
            sockel: this.sockel ? this.sockel.toJSON() : null,
            sockelOption: this.sockelOption,
            sockelHoehe: this.sockelOption == "ohne" ? null : this.sockelHoehe,
            schwebetuer: this.isSchwebetuer ? this.schwebetuer.toJSON() : null,
            schenkel: this.schenkel,
            isVerdeckt: this.isVerdeckt,
            modell: this.modell.Name,
            createdFromInspiration: this.createdFromInspiration,
            schliessdaempfung: this.schliessdaempfung,
            passleiste: this.passleiste,
            withRueckwand: this.withRueckwand,
            withDeckel: this.withDeckel,
            withExzenterverbindung: this.withExzenterverbindung
        };
    }

    fromJSON(json) {
        this.modell = _.find(APP.metadata.Modelle, (value, key) => {
            return value.Name === json.modell;
        });
        this.checkModell();
        this.createdFromInspiration = json.createdFromInspiration || (APP.arrangement.schraenke.length === 1 ? APP.configuration?.Code : null);
        if(Commons.isEmpty(json.griffArtikelnummer) && APP.arrangement.currentGriffArtikelnummer != null) {
            this.griffArtikelnummer = APP.arrangement.currentGriffArtikelnummer;
        } else {
            this.griffArtikelnummer = Commons.isEmpty(json.griffArtikelnummer) ? this.defaultGriff : json.griffArtikelnummer;
        }
        this.schiebetuerLinksVorne = json.schiebetuerLinksVorne || false;
        this.schliessdaempfung = json.schliessdaempfung || false;
        this.fluegelanordungLinks = json.fluegelanordungLinks || false;
        this.passleiste = json.passleiste || {
            senkrechteLinks: false,
            senkrechteRechts: false,
            schraege: false,
            oben: false,
        };
        this.angleSide = json.angleSide || this.options.getValue("dachschraege") || "none";
        this.size = v3().fromJSON(json.size || { x: 0, y: 0, z: 0 });
        this.sizeTop = v3().fromJSON(json.sizeTop || { x: 20, y: 20, z: 20 });
        this.elements = Commons.mapreuse(
            json.elements,
            this.elements,
            (e) => new Element(this),
            (d, s) => {
                return d.fromJSON(s);
            }
        );

        this.offset = json.offset ? json.offset : 0;
        this.rotation = json.rotation ? json.rotation : false;

        this.artikelnummer = json.artikelnummer || "Schrank";
        const jsonSchwebetuer = _.get(json, "schwebetuer");
        if (this.schwebetuer && jsonSchwebetuer) {
            this.schwebetuer.fromJSON(jsonSchwebetuer);
        }
        this.passleiste = json.passleiste;
        _.assign(this.sockel, json.sockel);
        this.sockelHoehe = json.sockelHoehe || -1;
        this.sockelOption = json.sockelOption || "ohne";
        this.withRueckwand = json.withRueckwand===undefined ? true : json.withRueckwand;
        this.withDeckel = json.withDeckel===undefined ? true : json.withDeckel;
        this.setNoRueckwand(!this.withRueckwand);
        this.setNoDeckel(!this.withDeckel);
        this.setExzenterverbindungOption(json.withExzenterverbindung)
        this.heal();
        return this;
    }

    checkModell() {
        if(!this.modell && _.values(APP.metadata.Modelle).length > 0) {
            this.modell = APP.metadata.Modelle['Standard'];
        }
    }

    heal() {
        // Das kann nur passieren, wenn ein Modell zur Laufzeit verändert wurde!
        // Dann sollte dieser Schrank die Standard Maße des Modells annehmen!
        // Außerdem sollten die options resettet werden, dann werden sie durch den getter "options" neu ermittelt!
        if (this.options && this._options.MODELL !== this.modell.Name) {
            this._options = null;
            this.size.x = this.options.getFloat("schrank_breite");
            this.size.y = this.options.getFloat("schrank_hoehe");
            this.size.z = this.options.getFloat("schrank_tiefe");
            this.sizeTop.x = this.options.getFloat("kniestock_breiteoben");
            this.sizeTop.y = this.options.getFloat("kniestock");
            this.sizeTop.z = this.options.getFloat("kniestock_hinten");
            this.angleSide = this.options.getValue("dachschraege") || "none";
        }

        if (Commons.isEmpty(this.elements)) {
            let defaultElementCount = this.options.getFloat("anzahl_elemente");
            if (!defaultElementCount) defaultElementCount = 3;
            this.setNumberOfElements(defaultElementCount);
        }
        if(Commons.isEmpty(this.passleiste)) {
            this.passleiste = {
                senkrechteLinks: false,
                senkrechteRechts: false,
                schraege: false,
                oben: false
            };
        }
    }

    // ----- GETTER AND SCHRANK-INFORMATION ------
    get parent() {
        return this.arrangement;
    }

    get arrangement() {
        return APP.arrangement;
    }

    get worldNormale() {
        const vector = v3(0, 0, 1);
        const matrix = new THREE.Matrix4().makeRotationFromQuaternion(this.quaternion);
        vector.applyMatrix4(matrix);
        return vector.round();
    }

    get options() {
        if (!this._options) {
            this._options = _.merge(new SchrankSettingsMixin(this.modell.Name), APP.options[this.modell.Name]);
            return this._options;
        } else {
            return this._options;
        }
    }

    get isPartOfEcksystem() {
        return this.connectors.find(connector => connector.rotation);
    }

    get hasRueckwand() {
        return this.options.getBool("rueckwand");
    }

    get connectors() {
        return APP.arrangement.schrankConnectors.filter((connector) => connector.schraenke.includes(this));
    }

    get rotationConnectors() {
        return this.connectors.filter((connector) => {
            return connector.rotation === true;
        });
    }

    get defaultGriff() {
        return this.options.getValue("default_griff", "Bogengriff");
    }

    get isSchwebetuer() {
        return this.options.getBool("is_schwebetuerenschrank");
    }

    get isSchiebetuerVorneAuswahl() {
        return this.isSchwebetuer && APP.arrangement.selectedSchrank?.schwebetuer?.schwebetuerFluegel?.length == 2;
    }

    get hasSchliessDaempfungsOption() {
        return this.isSchwebetuer;
    }

    get hasTopSchubladen() {
        return this.options.getBool("has_top_schubladen");
    }

    get needsPassleiste() {
        const produkt = APP.findProduct("Passleiste");
        const hasPassleiste = this.options.getBool("passleiste") && produkt?.PreisStandard != 0;
        // && !produkt.hidden;
        return hasPassleiste;
    }

    get dachschraegeSeite() {
        return ["left", "right"].includes(this.angleSide) ? this.angleSide : "none";
    }

    get passleisteLaenge() {
        const maße = this.schraegeMaße;
        const ueberstand = this.options.getFloat('passleistenueberstand') || 0;
        let laenge = [];
        _.forEach(maße, (v, k) => {
            if (this.passleiste?.[k]) {
                laenge.push(Math.ceil(v) + 2*ueberstand);
            }
        });

        return laenge;
    }

    get schraegeMaße() {
        const isSchraege = this.dachschraegeSeite!='none';
        const maße = { senkrechteLinks: 0, senkrechteRechts: 0, schraege: 0, oben: 0 };
        const sizeSchraege = this.sizeTop;  //sizeTop = x: Breite Oben, y: Kniestock, z: Tiefe oben
        const size = this.size;

        if (!isSchraege){
            maße.oben=size.x;
            maße.senkrechteLinks = maße.senkrechteRechts = size.y;
            maße.schraege = 0;
        }
        else{
            maße.oben=sizeSchraege.x;
            if (this.dachschraegeSeite=='right'){
                maße.senkrechteLinks = size.y;
                maße.senkrechteRechts = sizeSchraege.y
            }
            else{
                maße.senkrechteRechts = size.y;
                maße.senkrechteLinks = sizeSchraege.y
            }
            const a = size.x - sizeSchraege.x;
            const b = size.y - sizeSchraege.y;
            maße.schraege = Math.sqrt(a * a + b * b);
        }
        return maße;
    }

    get child3dContainer() {
        return this.object3d;
    }

    get schrank() {
        return this;
    }

    get matrix() {
        this._maybeUpdateMatrix();
        return this._matrix;
    }

    get inverseMatrix() {
        this._maybeUpdateMatrix();
        return this._matrix;
    }

    get rueckwandOuterZOffset() {
        return this.options.getFloat("rueckwand_vorsprung") - this.options.getFloat("rueckwand_dicke");
    }

    get rueckwandInnerZOffset() {
        return this.options.getFloat("rueckwand_vorsprung");
    }

    get isVerdeckt() {
        return this.verdeckungsIntervale.length > 0;
    }

    get verdeckteSchraenke() {
        const schraenke = [];
        this.arrangement.schraenke.forEach((schrank) => {
            schrank.elements.forEach((element) => {
                //console.log(element.unverdeckt.linksVerdeckt || element.unverdeckt.rechtssVerdeckt);
                if (element.unverdeckt.linksVerdeckt || element.unverdeckt.rechtssVerdeckt) {
                    //console.log(element, schrank);
                }
            });
        });
        return schraenke;
    }

    /**
     * Gibt immer die Aussenmasse an. Bei verdeckten Eckschränken wird die Breite des Gesamtschrankes mit beiden Flügeln zurückgegeben.
     * @returns {*}
     */
    get sizeOuter() {
        const arrangementSize = this.arrangement.getBox3World().getSize();
        
        const x = this.isVerdeckt ? this.size.x : Math.abs(this.drehungAbsolut) < 45 ? arrangementSize.x : arrangementSize.z;
        const y = arrangementSize.y + (this.sockelOption == "mitBlende" ? this.sockelHoehe : 0.0);
        const z = this.size.z + (this.sizeZisInnenmass ? this._getFrontOffset() : 0);

        return v3(x, y, z);
    }

    /**
     * gibt den absoluten Drehwinkel des Schrankes in Grad zurück. 0° = wie ein einzelner Standard-Schrank orientiert
     */
    get drehungAbsolut() {
        const z_ = v3(0, 0, 1).applyQuaternion(this.quaternion);
        return (Maths3.angleClockwise(Maths3.vector.z, z_, Maths3.vector.y) * 180) / Math.PI;
    }

    get hasInterior() {
        return this.elements.some(el => el.interieurs.length > 0);
    }

    isOrphaned() {
        if (this.arrangement == null) {
            return true;
        }
        if (!this.arrangement.schraenke.includes(this)) {
            return true;
        }
        return this.arrangement.isOrphaned();
    }

    isChildOf(parent) {
        return this.arrangement === parent;
    }

    hasLeftSeitenWand() {
        if(this.isPartOfEcksystem && this.options.getBool('offenes_eckelement') && this.isRightEckschrank() && !this.isVerdeckt) {
            return false;
        }
        return true;
    }

    hasRightSeitenwand() {
        if(this.isPartOfEcksystem && this.options.getBool('offenes_eckelement') && this.isLeftEckschrank() && !this.isVerdeckt) {
            return false;
        }
        return true;
    }
    
    isWithoutExterieurWall() {
        return this.isPartOfEcksystem && this.options.getBool("offenes_eckelement") && !this.isVerdeckt;
    }

    countExterieurWalls() {
        let count = 0;
        if(this.hasLeftSeitenWand()) count++;
        if(this.hasRightSeitenwand()) count++;
        return count;
    }

    isLeftEckschrank() {
        if(APP.arrangement.schraenke.length < 1 || !this.isPartOfEcksystem) return false;
        const rightNeighbor = this.neighbors["right"];
        if(!(rightNeighbor instanceof Schrank)) return false;
        const connector = SchrankConnector.findConnector(this, rightNeighbor);
        if(!connector.rotation) {
            return false;
        }
        return true;
    }

    isRightEckschrank() {
        if(APP.arrangement.schraenke.length < 1 || !this.isPartOfEcksystem) return false;
        const rightNeighbor = this.neighbors["left"];
        if(!(rightNeighbor instanceof Schrank)) return false;
        const connector = SchrankConnector.findConnector(this, rightNeighbor);
        if(!connector.rotation) {
            return false;
        }
        return true;
    }

    isEckSchrankWith(schrank) {
        return this.rotationConnectors.some((connector) => connector.schraenke.includes(schrank) && schrank !== this);
    }

    inEckeFor(schrank) {
        return this.rotationConnectors.some((connector) => {
            return connector.schraenke.includes(schrank) && schrank !== this && connector.isBackSchrank(this);
        });
    }

    getPossibleForms() {
        if (!APP.configuration.Code && APP.arrangement.schraenke.length === 1) {
            return _.values(_.filter(APP.metadata.Modelle, (model) => !model.Versteckt));
        } else {
            const allNeighbors = this.connectors.map((connector) => {
                const thisPosition = _.findKey(connector.layout, (value, key) => APP.arrangement.schraenke[value] === this);
                const otherSchrank = connector.getOtherSchrank(this);
                // debugger;
                return otherSchrank.possibleNeighborCombinations[thisPosition]
                    .filter((possibleNeighbor) => possibleNeighbor.rotation === connector.rotationPossible)
                    .map((possibleNeighbor) => possibleNeighbor.addable);
            });
            // debugger;
            const possibleNeighbors = _.intersection(...allNeighbors);
            return possibleNeighbors;
        }
    }

    getMaterialArtikelnummer(product, interieur) {
        if (!Commons.isEmpty(product)) {
            let side = product.Dekortyp;
            let materialArtikelnummer;
            if (side === "corpus") {
                materialArtikelnummer = this.materials.corpus;
            } else if (side === "front" && interieur) {
                materialArtikelnummer = interieur.material;
            }
            let materialProdukt = APP.findProduct(materialArtikelnummer);
            if (materialProdukt && materialProdukt.InnenMattAlternative && product.MattAlternative) {
                materialArtikelnummer = materialProdukt.InnenMattAlternative;
            }
            return materialArtikelnummer;
        }
        return null;
    }

    getQuaternionForDirection(key) {
        switch (key) {
            case "left":
                return new THREE.Quaternion().copy(this.quaternion).multiply(Maths3.quaternion.y90);
            case "right":
                return new THREE.Quaternion().copy(this.quaternion).multiply(Maths3.quaternion.y270);
            case "top":
                return new THREE.Quaternion().copy(this.quaternion);
            case "bottom":
                return new THREE.Quaternion().copy(this.quaternion);
        }
    }

    getOffsetForNeighborAndDirection(neighbor) {
        if (neighbor.offset) {
            return neighbor.offset;
        } else {
            const connector = SchrankConnector.findConnector(this, neighbor);
            if (connector) {
                return connector.offset;
            }
        }
        return 0;
    }

    toBoxPosition(key, rotation, otherInEcke) {
        return SchrankConnector.toBoxPosition(key, rotation, otherInEcke);
    }

    whatBoxPosition(key, rotation, otherInEcke) {
        return SchrankConnector.whatBoxPosition(key, rotation, otherInEcke);
    }

    _getFrontOffset() {
        let offset = 0;
        this.elements.forEach((element) => element.interieurs.forEach((interieur) => (offset = Math.max(offset, interieur.getFrontOffset()))));
        if (this.isSchwebetuer) {
            offset = Math.max(offset, this.schwebetuer.getFrontOffset());
        }
        if (offset > 0) {
            offset += this.options.getFloat("korpus_extra_offset");
        }
        return offset;
    }

    getInterieurMinMaxBoxWithoutTueren() {
        const box = new THREE.Box3();
        this.elements.forEach((element) => box.union(element.getInterieurMinMaxBoxWithoutFullHeightDrag()));
        return box;
    }

    getBox3World(unionBox = new THREE.Box3()) {
        if(this.box) {
            this.box.getCorners().forEach((v) => {
                v.applyMatrix4(this.matrix);
                unionBox.expandByPoint(v);
            });
        }
        return unionBox;
    }

    schrankToLocal(v) {
        return v.clone();
    }

    localToSchrank(v) {
        return v.clone();
    }

    localToWorldBox(b: THREE.Box3): THREE.Box3 {
        return new THREE.Box3(b.min.applyMatrix4(this.inverseMatrix), b.max.applyMatrix4(this.inverseMatrix));
    }

    localToWorld(vector) {
        return vector.applyMatrix4(this.matrix);
    }

    worldToLocal(vector) {
        return vector.applyMatrix4(this.inverseMatrix);
    }


    

    // ----- SETTER AND FUNCTIONS -----

    selectElement(elementNo) {
        this.elements.forEach((e) => (e.selected = false));
        if (elementNo >= 0) {
            this.elements[elementNo].selected = true;
        }
    }

    delete() {
        // ACHTUNG: Hier wird mit model.setDirty gearbeitet, also gibt es kein trycompile mit Fehlerbehandlung.
        // Also hier nur sichere dinge tun..
        this.deleted = true;
        let updateNeeded = false;
        APP.arrangement.schraenke.forEach((schrank) => {
            _.forEach(schrank.neighbors, (value, key) => {
                if (value === this) {
                    schrank.neighbors[key] = null;
                    updateNeeded = true;
                }
            });
        });

        if(updateNeeded) {
            APP.model.setDirty();
        }
    }

    setNumberOfElements(numOfElemente) {
        const minElemente = this.options.getMinValue("anzahl_elemente", 1);
        const maxElemente = this.options.getMaxValue("anzahl_elemente", 1000);
        numOfElemente = Maths.cutBetween(minElemente, numOfElemente, maxElemente);
        this.elements = this.elements || [];
        this.setArrayLength(this.elements, numOfElemente, () => new Element(this), undefined, this.isLeftEckschrank());
    }

    setNoRueckwand(value) {
        this.withRueckwand = !value;
        if (value) {
            this.rueckwand.exists = false;
        }
        else {
            this.rueckwand.exists = true;
        }
    }
    setNoDeckel(value) {
        this.withDeckel = !value;
        if (value) {
            this.deckel.exists = false;
        }
        else {
            this.deckel.exists = true;
        }
    }
    setExzenterverbindungOption(value) {
        this.withExzenterverbindung = value;
    }

    /**
     * Setzt die Anzahl der Elemente eines Arrays auf den Parameter count, und löscht oder erzeugt dazu Elemente.
     * @param array
     * @param count
     * @param createElementCallback
     * @param removeElementCallback
     * @return {Array}
     */
    setArrayLength(array, count, createElementCallback, removeElementCallback = undefined, useUnshift) {
        while (count > array.length) {
            if (useUnshift) {
                array.unshift(createElementCallback(array.length));
            } else {
                array.push(createElementCallback(array.length));
            }
        }
        if (array.length > count) {
            if (removeElementCallback) {
                for (let i = count - 1; i < array.length; i++) {
                    removeElementCallback(array[i]);
                }
            }
            array.splice(count, array.length - count);
        }
        return array;
    }

    setHeightToElementMaximum() {
        const elementMaximum = this.elements.reduce((c, e) => Math.max(c, e.getOuterHeight()), 0);

        // if(this.sockelOption == "mitBlende" && this.sockelHoehe > 0) {
        //     elementMaximum += this.schrank.sockelHoehe;
        // }
        
        this.setDimension("y", elementMaximum);
    }

    setDimension(dir, value, force) {
        if (this.size[dir] !== value || force) {
            this.iterateInterieur(FullHeightDragable.maybeSetFollowTopOfElement);
            this.size[dir] = value;
            this._checkSizes("setDimension");
            this.compileBox();
            this.connectors
                .filter((connector) => connector.linked[dir])
                .forEach((connector) => {
                    const connectedSchrank = connector.getOtherSchrank(this);
                    connectedSchrank.setDimension(dir, value);
                });
        }
    }

    setDimensionTop(dir, value) {
        this.iterateInterieur(FullHeightDragable.maybeSetFollowTopOfElement);
        this.sizeTop[dir] = value;
        this._checkSizes("setDimensionTop");
        this.compileBox();
    }

    clearLocked() {
        this.elements.forEach((e) => e.clearLocked());
    }

    setModell(modell) {
        this.modell = modell;
        this.heal();
    }

    async setFromInspiration(inspiration_code) {
        const inspiration = await (await fetch(`${APP.settings.siteUrl}?Code=${inspiration_code}&api=1&fmt=configuration`)).json();
        const newSchrankJson = inspiration.Json.schraenke[inspiration.Json.currentSchrank];
        this.fromJSON({
            ...newSchrankJson,
            createdFromInspiration: inspiration_code,
        });
        this.reset();
        this.heal();
    }

    setMaterialArtikelnummer(materialArtikelnummer, side, interieur) {
        //Wofür wird gesetzt, Interieur oder Schrank?
        if (side === "corpus") {
            this.materials.corpus = materialArtikelnummer;
        } else if (side === "front") {
            interieur.material = materialArtikelnummer;
            /* this.materials.last = materialArtikelnummer; */
        }
    }

    reset() {
        this.possibleNeighborCombinations = null;
        _.forEach(this.neighbors, (neighbor, key) => {
            if (!(neighbor instanceof Schrank)) {
                this.neighbors[key] = null;
            }
        });
    }

    copy(schrank, createInterceptor) {
        this.angleSide = schrank.angleSide;
        this.direction = schrank.direction;
        this.size.copy(schrank.size);
        this.sizeTop.copy(schrank.sizeTop);
        this.elements = Commons.mapreuse(
            schrank.elements,
            this.elements,
            () => new Element(this),
            (d, s) => d.copy(s, createInterceptor)
        );
        // location brauchen wir bei Drag&Drop nicht...
        return this;
    }

    iterateElements(iterator) {
        this.elements.forEach((e) => e.iterateElements(iterator));
    }

    iterateInterieur(iterator) {
        this.iterateElements((e) => e.interieurs.forEach((x) => x.iterate(iterator)));
    }

    initCoordinateSystem() {
        this._matrix = new THREE.Matrix4();
        this._inverseMatrix = new THREE.Matrix4();
        const setMatrixNeedsUpdate = () => {
            this._matrixNeedsUpdate = true;
        };
        this.position = THREE.Vector3.initWithObservation(setMatrixNeedsUpdate);
        this.quaternion = new THREE.Quaternion().onChange(setMatrixNeedsUpdate);
    }

    _maybeUpdateMatrix() {
        if (this._matrixNeedsUpdate) {
            this._matrix.compose(this.position, this.quaternion, Maths3.vector.one);
            this._inverseMatrix.getInverse(this._matrix);
            this._matrixNeedsUpdate = false;
        }
    }

    iterate(iterator) {
        iterator(this);
        this.elements.forEach((x) => x.iterate(iterator));
        this.innerWalls.forEach((x) => x.iterate(iterator));
        this.outerWalls.left && iterator(this.outerWalls.left);
        this.outerWalls.right && iterator(this.outerWalls.right);
        this.deckel && iterator(this.deckel);
        this.rueckwand && iterator(this.rueckwand);
        this.schrankboden && iterator(this.schrankboden);
        this.sockel && iterator(this.sockel);
        this.schwebetuer && iterator(this.schwebetuer);
    }

    onTap() {
        APP.arrangement.selectedSchrank = this;
        APP.uiHandler.ui.showSidebar("korpus");
    }

    // ----- COMPILE LAYER -----

    compile() {
        // multiSchrank (jeder Schrank kümmert sich um seine Nachbarn)
        // Verbindungen zu Nachbarn werden über SchrankConnectoren abgebildet
        this.compilePossibleNeighborCombinations();
        this.compileNeighbors();
        this.compileRotations();
        this.compileBox();
        this.compileVerdeckungen();
        this.compileBox();
        this.compileUiSpheres();
        this.compileLocations();

        this.compileVerdeckungen();
        this.compileInnen();
        this.compileCameraPosition();

        this.compiled = true;
    }

    compileVerdeckungen() {
        this.resetVerdeckung();
        this.connectors
            .filter((connector) => connector.rotation)
            .forEach((connector) => {
                const otherSchrank = connector.getOtherSchrank(this);
                APP.arrangement.compileVerdeckungForSchraenke(this, otherSchrank);
            });
    }

    resetVerdeckung() {
        this.verdeckungsIntervale = [];
        this.elements.forEach((e) => {
            e.verdecktAnderenSchrank = null;
            e.unverdeckt.linksVerdeckt = false;
            e.unverdeckt.rechtsVerdeckt = false;
            e.unverdeckt.box.copy(e.box);
            e.unverdeckt.size.copy(e.size);
            e.verdecktVonElementen = [];
        });
    }

    compileCameraPosition() {
        const position = new THREE.Vector3().copy(this.position);
        const cameraDistance = 100;
        const oeffnungsvector = v3(0, 0, 1);
        this.cameraPosition = position.add(oeffnungsvector.multiplyScalar(cameraDistance));
    }

    compileNeighbors() {
        _.forEach(this.neighbors, (value, key) => {
            if (value?.deleted) {
                this.neighbors[key] = null;
            }
        });

        this.connectors.forEach((connector) => {
            const thisIndex = APP.arrangement.schraenke.indexOf(this);
            const otherIndex = connector.indices.find((index) => index !== thisIndex);
            const neighborKey = _.findKey(connector.layout, (value, key) => {
                return value === otherIndex;
            });
            this.neighbors[neighborKey] = APP.arrangement.schraenke[otherIndex];

            if (!this.neighbors[neighborKey].box) {
                this.neighbors[neighborKey].compileBox();
            }
        });
    }

    compileRotations() {
        _.forEach(this.neighbors, (neighbor, key) => {
            if (this.isEckSchrankWith(neighbor)) {
                const quaternion = this.getQuaternionForDirection(key);
                neighbor.quaternion.copy(quaternion);
            } else {
                neighbor?.quaternion.copy(this.quaternion);
            }
        });
    }

    // Bestimmt aus metadata welche Modelle an welche Stelle neben diesen Schrank gestellt werden können
    compilePossibleNeighborCombinations() {
        if (!this.possibleNeighborCombinations) {
            //init
            this.possibleNeighborCombinations = {
                top: [],
                left: [],
                right: [],
                bottom: [],
            };

            const combinationsForThisModelFromLeft = APP.metadata.multiSchrankKombinationen.filter(
                (c) => c.Inspiration_1 === this.createdFromInspiration
            );

            const combinationsForThisModelFromRight = APP.metadata.multiSchrankKombinationen.filter(
                (c) => c.Inspiration_2 === this.createdFromInspiration
            );

            combinationsForThisModelFromLeft.forEach((combination) => {
                let direction = combination.Position.split("_");
                this.possibleNeighborCombinations[direction[0]].push({
                    addable: combination.Inspiration_2,
                    abstand: combination.Abstand,
                    rotation: combination.Drehbar,
                });
            });

            combinationsForThisModelFromRight.forEach((combination) => {
                let direction = combination.Position.split("_");
                this.possibleNeighborCombinations[direction[1]].push({
                    addable: combination.Inspiration_1,
                    abstand: combination.Abstand,
                    rotation: combination.Drehbar,
                });
            });
        }

        return this.possibleNeighborCombinations;
    }

    compileLocations() {
        _.forEach(this.neighbors, (neighbor, key) => {
            if (neighbor) {
                const whatBoxPosition = neighbor.whatBoxPosition
                    ? neighbor.whatBoxPosition(key, this.isEckSchrankWith(neighbor), this.inEckeFor(neighbor))
                    : v3(0, 0, 0);
                const toBoxPosition = neighbor.toBoxPosition
                    ? neighbor.toBoxPosition(key, this.isEckSchrankWith(neighbor), this.inEckeFor(neighbor))
                    : v3(0, 0, 0);
                    
                // if(neighbor.sockelOption == "mitBlende" && this.sockelOption != "mitBlende") {
                //     toBoxPosition.y -= Math.max(this.sockelHoehe, 0)/100
                // }
                
                /* if (neighbor instanceof Schrank) {
                    console.log("whatBoxPosition", APP.arrangement.schraenke.indexOf(this), key, whatBoxPosition, "inEckeFor", this.inEckeFor(neighbor));
                    console.log("toBoxPosition", APP.arrangement.schraenke.indexOf(this), key, toBoxPosition, "inEckeFor", this.inEckeFor(neighbor));
                } */

                const position = toBoxPosition ? this.box.grid(toBoxPosition.x, toBoxPosition.y, toBoxPosition.z) : v3(0, 0, 0);
                const offSetForNeighborAndDirection = this.getOffsetForNeighborAndDirection(neighbor, key);

                const toPositionWithOffset = this.calculatePositionWithOffset(
                    position,
                    key,
                    offSetForNeighborAndDirection,
                    this.isEckSchrankWith(neighbor),
                    this.inEckeFor(neighbor)
                );
                // Der Nachbar kann selbst auch sein what bestimmen, wenn er das nicht macht, wird sein Ursprung als Default genommen
                const what = whatBoxPosition
                    ? neighbor.box.grid(whatBoxPosition.x, whatBoxPosition.y, whatBoxPosition.z)
                    : neighbor.box.grid(0, 0, 0);
                Scenes.bring({
                    what: [neighbor, what],
                    // Das ist ein bisschen blöd gelost. Besser und sicherer wäre die Frage ob es Teil des child3dContainers ist
                    to: neighbor instanceof Schrank ? [this, toPositionWithOffset] : [toPositionWithOffset],
                    translate: true,
                });
            }
        });
    }

    calculatePositionWithOffset(position, direction, offset, isPartOfEckschrank, thisIsInEcke) {
        const newPosition = SchrankConnector.calculatePositionWithOffset(position, direction, offset, isPartOfEckschrank, thisIsInEcke);
        // if(this.sockelOption == "mitBlende" && this.sockelHoehe > 0) {
        //     newPosition.y += this.sockelHoehe;
        // }
        return newPosition;
    }

    // Für jede mögliche Nachbarkombination an der noch kein Schrank oder keine Sphere steht wird eine Sphere erstellt
    // Diese wird auch von hier compiled
    compileUiSpheres() {
        _.forEach(this.possibleNeighborCombinations, (neighbor, key) => {
            if (!this.neighbors[key] && neighbor.length > 0) {
                const direction = key;
                const uiSphere = new UiSphere(this, direction);
                this.neighbors[key] = uiSphere;
            }
            if (this.neighbors[key] && !(this.neighbors[key] instanceof Schrank)) {
                this.neighbors[key].compile();
            }
        });
    }

    compileBox() {
        this.product = APP.findProduct(this.artikelnummer);
        this._compileElementWidthAndNumber();
        this._checkSizes();
        this._compileSchrank();
        this._compileElementeBoxes();
        if (this.isSchwebetuer) {
            this.schwebetuer.compile();
        }
    }

    compileInnen() {
        if (this.options.getBool("sockel") && this.sockelOption != "ohne") {
            if (!this.sockel) {
                this.sockel = new Sockel(this);
                if (this.sockelHoehe == -1) {
                    this.sockelHoehe = this.options.getFloat("minimaleSockelhoehe");
                    APP.arrangement.selectedSchrank.sockelHoehe = this.sockelHoehe;
                }
            }
            this.sockel.compile();
        } else {
            this.sockel = null;
        }
        
        this.schrankboden.compile();
        this.deckel.compile();
        this._compileAussenwaende();
        this._compileInnenwaende();
        if (this.hasRueckwand) {
            this.rueckwand.compile();
        }
        this.elements.forEach((element) => element.compileParts());
        this._compileFrontCounts();
        // this._compileMaterialCounts();
        this.elements.forEach((element) => element.compileFronts());
        this._compileFuesse();

        this._checkForOffeneElemente();

        this._compileFixedSchubladen();
    }

    _compileFixedSchubladen() {

        this.elements.forEach((element) => {
            element._compileFixedTopSchublade(this.hasTopSchubladen);
        });

    }

    _compileElementWidthAndNumber() {
        const aussenwandCount = this.countExterieurWalls();
        const brettSpaceFormula = () =>
            aussenwandCount * this.options.getFloat("aussenwand_staerke") + (this.elements.length - 1) * this.options.getFloat("brett_staerke");
        let freeInnerSize = Helper.round(this.size.x - brettSpaceFormula());
        let elementSpreadCount = this.elements.length;
        let lockedInnerSize = 0;
        let elementId = 0;
        this.elements.forEach((element) => {
            if (element.locked.x) {
                freeInnerSize -= element.size.x;
                lockedInnerSize += element.size.x;
                elementSpreadCount -= 1;
            }
            elementId++;
        });
        let elementVariableWidth = freeInnerSize / elementSpreadCount;
        if (elementSpreadCount === 0) {
            this.size.x = Helper.round(lockedInnerSize + brettSpaceFormula());
        } else {
            const elementsOutOfMinMax = [];
            let tries = 100;
            do {
                elementsOutOfMinMax.length = 0;
                let leftInnerSize = freeInnerSize;
                let elementLeftCount = elementSpreadCount;
                this.elements.forEach((element) => {
                    if (!element.locked.x) {
                        const minMax = element.getMinMax(false);
                        if (elementVariableWidth < minMax.min.x) {
                            leftInnerSize -= minMax.min.x;
                            element.size.x = minMax.min.x;
                            elementLeftCount -= 1;
                            elementsOutOfMinMax.push(element);
                        }
                        if (elementVariableWidth > minMax.max.x) {
                            leftInnerSize -= minMax.max.x;
                            element.size.x = minMax.max.x;
                            elementLeftCount -= 1;
                            elementsOutOfMinMax.push(element);
                        }
                    }
                });
                // Wird ggf. -/+Infinity - passt trotzdem
                elementVariableWidth = leftInnerSize / elementLeftCount;
                if (this.options.isWithinMinMaxValue("element_breite", elementVariableWidth)) {
                    break;
                }
                if (elementVariableWidth < this.options.getMinValue("element_breite")) {
                    const element = _.last(this.elements);
                    this.elements.splice(-1, 1);
                    freeInnerSize += this.options.getFloat("brett_staerke");
                    elementSpreadCount--;
                    if (elementSpreadCount === 0) {
                        break;
                        //this.elements.splice(-1, 1);
                    }
                } else if (elementVariableWidth > this.options.getMaxValue("element_breite")) {
                    this.elements.push(new Element(this));
                    freeInnerSize -= this.options.getFloat("brett_staerke");
                    elementSpreadCount++;
                    elementId++;
                }
                elementVariableWidth = freeInnerSize / elementSpreadCount;
                tries--;
            } while (tries > 0);
            if (this.elements.length === 0) {
                // Das hier ist echt zu hinterfragen: Kann im worst case die Breite des Schrankes manipulieren.. obwohl die ja ggf. gesetzt ist
                // Und bei nur einem Element kann die Schrank Mindestbreite unterschritten werden..
                elementVariableWidth = this.options.getMinValue("element_breite");
                this.elements.push(new Element(this));
            }
            let elementSize = 0;
            this.elements.forEach((element) => {
                element.deckel = [];
                element.rueckwand = [];
                if (!element.locked.x && elementsOutOfMinMax.indexOf(element) == -1) {
                    element.size.x = elementVariableWidth;
                }
                elementSize += element.size.x;
            });
            this.size.x = Helper.round(elementSize + brettSpaceFormula());
            // FIXME das in die Schleife integrieren
            this._checkForSeitenwandInKnick();
        }
    }

    _checkForOffeneElemente() {
        const openElements = this.elements.filter((e) => !(e.wallRight && e.wallLeft));
        openElements.forEach((oe) => {
            oe.interieurs
                .filter((i) => !(i.type === "Tuer"))
                .forEach((i) => {
                    console.log("Auto-removed Interieur from open Element: ", i);
                    i.removeFromElement();
                });
        });
    }

    _compileSchrank() {
        APP.model.lazyCreateObject3d(this, () => {
            const schrank = new SchrankO3(this.product, this);
            this.arrangement.object3d.schraenke.add(schrank);
            return schrank;
        });

        const getFussHoehe = () => {
            if(this.sockelOption != "ohne" && this.sockelHoehe > 0) {
                return this.sockelHoehe;
            } else {
                return this.options.getBool("puck_fuesse_anzeigen") ? this.options.getFloat("puck_fuesse_hoehe") : 0;
            }
        }

        const getBottomHeight = () => {
            if(this.sockelOption == "mitBlende") {
                return this.options.getFloat('brett_staerke');
            } else {
                return this.sockelOption != "ohne" ? this.sockelHoehe + this.options.getFloat('brett_staerke') : 0 + this.options.getFloat("boden_staerke"); //CHE: sockel_dicke ist bei allen Modellen gesetzt
            }
        }

        const fussHoehe = getFussHoehe();
        const bottomHeight = getBottomHeight();
        
        // die Box entspricht der BoundingBox des ganzen Schrankes
        this.box = new THREE.Box3(v3(0, 0, 0), this.size.clone());

        this.box.max.y = this.elements
            .filter((element) => element.locked.y)
            .reduce(
                (max, element) => Math.max(max, this.box.min.y + fussHoehe + bottomHeight + element.size.y + this.options.getFloat("deckel_staerke")),
                this.box.max.y
            )

        // TODO.. Was ist das nochmal? Ist raus genommen wordem im Zuge der neuen Verdeckungen
        /* if (!this.sizeZisInnenmass) {
            this.box.max.z -= this._getFrontOffset();
        } */

        this.corpusBoxWithFeet = this.box.clone(); // die CorpusBox entspricht dem Schrank ohne Füsse / Türen

        // TODO siehe oben
        /* if (this. ) {
            this.box.max.z -= this._getFrontOffset();
        } */ 
        this.corpusBox = this.corpusBoxWithFeet.clone();
        
        if(this.sockelOption == "mitBlende") {
            this.corpusBox.min.y += Math.max(0, this.sockelHoehe);
            this.corpusBox.max.y += Math.max(0, this.sockelHoehe);
        }
        // die ElementsBox entspricht dem Union der Innenmass-Boxen aller Elemente.
        this.elementsBox = this.corpusBox.clone();

        if(this.hasLeftSeitenWand()) {
            this.elementsBox.min.x += this.options.getFloat("aussenwand_staerke");
        }
        if(this.hasRightSeitenwand()) {
            this.elementsBox.max.x -= this.options.getFloat("aussenwand_staerke");
        }

        /* if(!this.hasLeftSeitenWand()) {
            this.elementsBox.max.x += 2 * this.options.getFloat("aussenwand_staerke");
        } else if(!this.hasRightSeitenwand()) {
            this.elementsBox.min.x -= 2 * this.options.getFloat("aussenwand_staerke");
        } */

        this.elementsBox.min.y += bottomHeight;

        if (this.withDeckel) {
            this.elementsBox.max.y -= this.options.getFloat("deckel_staerke") + this.options.getFloat("deckel_versatz_nach_unten");
        }
        this.elementsBox.min.z += this.rueckwandInnerZOffset;

        this.slopeX = this.angleSide === "left" ? this.size.x - this.sizeTop.x : this.sizeTop.x;

        this.hasFronts = this.elements.some((element) => element.getDoors().length > 0);
        this.hasInterieurDoors = this.elements.some((element) => element.interieurs.some((interieur) => interieur.hasFronts));

        const schrankOffsetY = this.sockelOption == "mitBlende" ? Math.max(0, this.sockelHoehe) : 0;
        this.slopings = new Slopings(this.size.y+schrankOffsetY, this.elementsBox.min.z, this);
        if (this.angleSide === "left") {
            this.slopings.sx = new Slopings.Sloping(0, this.sizeTop.y, this.slopeX, this.size.y+schrankOffsetY, this);
        } else if (this.angleSide === "right") {
            this.slopings.sx = new Slopings.Sloping(this.slopeX, this.size.y+schrankOffsetY, this.size.x, this.sizeTop.y, this);
        } else if (this.angleSide === "back") {
            this.slopings.sz = new Slopings.Sloping(0, this.sizeTop.y, this.size.z - this.sizeTop.z, this.size.y, this);
        }
    }

    _checkForSeitenwandInKnick() {
        if (this.angleSide === "left" || this.angleSide === "right") {
            const wandStaerke = this.options.getFloat("brett_staerke");
            for (let i = 0; i < this.elements.length - 1; i++) {
                const element = this.elements[i];
                if (this.slopeX > element.box.max.x && this.slopeX < element.box.max.x + wandStaerke) {
                    const oldBox = element.box.clone();
                    const newBox = element.box.clone();
                    newBox.max.x = this.angleSide === "left" ? this.slopeX - wandStaerke : this.slopeX;
                    const xModifier = oldBox.getSize().x - newBox.getSize().x;
                    element.size.x -= xModifier;
                    this.elements[i + 1].size.x += xModifier;
                }
            }
        }
    }

    _compileElementeBoxes() {
        let elementXMin = this.options.getFloat("aussenwand_staerke");
        if(!this.hasLeftSeitenWand()) {
                elementXMin = 0;
        }
        const brett_staerke = this.options.getFloat("brett_staerke");
        this.elements.forEach((element, index) => {
            element.compileBox(elementXMin);
            elementXMin += element.size.x + brett_staerke;
        });
    }

    _compileAussenwaende() {
        const maybeCreateWall = (side) => {
            if (this.outerWalls[side] === null) {
                this.outerWalls[side] = new Brett(this, "Aussenwand", "yz");
            }
        };
        if (this.countExterieurWalls() < 2) {
            if (!this.hasLeftSeitenWand()) {
                this.outerWalls.left = null;
                _.first(this.elements).wallLeft = null;
            } else if(!this.hasRightSeitenwand()) {
                this.outerWalls.right = null;
                _.last(this.elements).wallRight = null;
            }
        } else {
            maybeCreateWall("left");
            maybeCreateWall("right");
        }
        Object.keys(this.outerWalls).forEach((w, index) => {
            if (this.outerWalls[w]) {
                this._compileAussenwand(w, this.outerWalls[w]);
                this._wandGrater(this.outerWalls[w]);
            }
        });
    }

    _compileAussenwand(side, wall) {
        wall.box = this.corpusBox.clone();

        if (side === "left") {
            _.first(this.elements).wallLeft = wall;
        } else {
            _.last(this.elements).wallRight = wall;
        }

        if (side === "left") {
            wall.box.max.x = wall.box.min.x + this.options.getFloat("aussenwand_staerke");
        } else {
            wall.box.min.x = wall.box.max.x - this.options.getFloat("aussenwand_staerke");
        }

        if (!this.options.getBool("aussenwand_durchgehend_unten")) {
            wall.box.min.y = this.elementsBox.min.y;
        }

        if (side === "left" && _.first(this.elements).locked.y) {
            wall.box.max.y = this.elementsBox.min.y + _.first(this.elements).size.y + this.options.getFloat("deckel_staerke");
        } else if (side === "right" && _.last(this.elements).locked.y) {
            wall.box.max.y = this.elementsBox.min.y + _.last(this.elements).size.y + this.options.getFloat("deckel_staerke");
        }

        if (!this.deckel.needEinzelneDeckel && !this.options.getBool("aussenwand_durchgehend_oben", false)) {
            wall.box.max.y -= this.options.getFloat("deckel_staerke");
        }

        if (this.isSchwebetuer) {
            const schiebetuer_vorsprung_min = this.options.getMinValue("schiebetuer_vorsprung", 2.5);
            const schiebetuer_vorsprung_max = this.options.getMaxValue("schiebetuer_vorsprung", 5);
            let offset =
                schiebetuer_vorsprung_min -
                this.options.getFloat("schiebetuer_griffleiste_staerke", 0.3) -
                this.options.getFloat("schiebetuer_abstand_aussenwand", 0.5);

            if (side === (this.arrangement.schiebetuerLinksVorne ? "left" : "right") && this.elements.length % 2 === 0) {
                offset =
                    schiebetuer_vorsprung_min +
                    schiebetuer_vorsprung_max -
                    this.options.getFloat("schiebetuer_griffleiste_staerke", 0.3) -
                    this.options.getFloat("schiebetuer_abstand_aussenwand", 0.5);
            }
            wall.box.max.z += offset;
        }

        wall.boxToVertices();
        wall.compile();
    }

    _compileInnenwaende() {
        const lazyWalls = APP.model.lazyPool(this, "innerWalls", () => {
            const brett = new Brett(this, "Seitenwand", "yz");
            brett.isInner = true;
            return brett;
        });
        const numberOfInnenwaende = this.elements.length - 1;

        const innerWallBox = this.elementsBox.clone();
        innerWallBox.min.z = this.corpusBox.min.z;
        if (this.deckel.needEinzelneDeckel) {
            innerWallBox.max.y += this.options.getFloat("deckel_staerke");
        }
        innerWallBox.max.z -= this.options.getFloat("aussenwand_vorsprung");

        for (let i = 0; i < numberOfInnenwaende; i++) {
            const element = this.elements[i];
            const nextElement = this.elements[i + 1];
            const wall = lazyWalls.next();
            wall.box = innerWallBox.clone();

            wall.box.min.x = element.box.max.x;
            wall.box.max.x = nextElement.box.min.x;

            if (element.locked.y || nextElement.locked.y) {
                wall.box.max.y = this.elementsBox.min.y + Math.max(element.size.y, nextElement.size.y) + this.options.getFloat("deckel_staerke");
            }

            element.wallRight = wall;
            nextElement.wallLeft = wall;
            this._wandGrater(wall);
        }
        lazyWalls.cleanup();
        this.innerWalls.forEach((wall) => wall.compile());
    }

    _compileFrontCounts() {
        // clear
        this.innerWalls.forEach((x) => (x.frontCount = 0));
        this.outerWalls.left ? (this.outerWalls.left.frontCount = 0) : null;
        this.outerWalls.right ? (this.outerWalls.right.frontCount = 0) : null;
        this.deckel.boeden?.forEach((x) => (x.frontCount = 0));
        this.schrankboden.frontCount = 0;
        this.elements.forEach((x) => x.konstruktionsboeden.forEach((x) => (x.frontCount = 0)));
        // count
        this.elements.forEach((element) => element.compileFrontCounts());
        // Korrektur für Deckel / Boden
        this.deckel.boeden?.forEach((x) => (x.frontCount = Math.min(1, x.frontCount)));
        this.schrankboden.frontCount = Math.min(1, this.schrankboden.frontCount);
    }

    _compileFuesse() {
        const radius = this.options.getFloat("puck_fuesse_durchmesser") / 2;
        const height = this.options.getFloat("puck_fuesse_hoehe");
        const abstand = this.options.getFloat("puck_fuesse_abstand");
        const vorsprung = this.options.getFloat("aussenwand_vorsprung");

        const lazyFuesse = APP.model.lazyPool(
            this,
            "fuesse",
            () => ({ position: v3() }),
            () => {
                const material = new THREE.MeshBasicMaterial({ color: this.options.getInt("puck_fuesse_farbe") });
                const geometry = new THREE.CylinderBufferGeometry(radius, radius, height, 32, 32);
                const cylinder = new THREE.Mesh(geometry, material);
                cylinder.vsUpdate = () => {
                    cylinder.position.copy(cylinder.vsData.position);
                };
                this.object3d.add(cylinder);
                return cylinder;
            }
        );

        if (this.options.getBool("puck_fuesse_anzeigen")) {
            const fuesse = _.range(4).map((x) => lazyFuesse.next());
            const box = this.box.clone().expandByVector(v3(-radius - abstand, 0, -radius - abstand - vorsprung));
            box.min.y = height / 2;
            fuesse[0].position.set(box.min.x, box.min.y, box.min.z);
            fuesse[1].position.set(box.min.x, box.min.y, box.max.z);
            fuesse[2].position.set(box.max.x, box.min.y, box.min.z);
            fuesse[3].position.set(box.max.x, box.min.y, box.max.z);
        }

        lazyFuesse.cleanup();
    }

    _checkSizes(initiator = null) {
        const check = (minmax , value , borderValue , title , message) => {
            if ((minmax == "min" && value < borderValue) || (minmax == "max" && value > borderValue)) {
                value = borderValue;
                APP.model.maybeThrowCompileError(this, title, message, borderValue);
            }
            return value;
        };

        const aussenwandCount = this.countExterieurWalls();

        const minElemente = this.options.getMinValue("anzahl_elemente", 1);
        const maxElemente = this.options.getMaxValue("anzahl_elemente", 1000);

        let maxHeightI18nKey = "maxHeightText";
        if (this.isSchwebetuer) {
            maxHeightI18nKey = "maxHeightTextAlternative";
        }
        this.size.x = check("min", this.size.x, this.options.getMinValue("schrank_breite"), "minWidth", "minWidthText");
        this.size.x = check("max", this.size.x, this.options.getMaxValue("schrank_breite"), "maxWidth", "maxWidthText");

        this.size.x = check(
            "min",
            this.size.x,
            minElemente * this.options.getMinValue("element_breite") +
                minElemente * this.options.getFloat("aussenwand_staerke") +
                (aussenwandCount === 2 ? this.options.getFloat("aussenwand_staerke") : 0),
            "minWidth",
            "minWidthText"
        );

        this.size.x = check(
            "max",
            this.size.x,
            maxElemente * this.options.getMaxValue("element_breite") +
                maxElemente * this.options.getFloat("aussenwand_staerke") +
                (aussenwandCount === 2 ? this.options.getFloat("aussenwand_staerke") : 0),
            "maxWidth",
            "maxWidthText"
        );

        this.size.x = check(
            "min",
            this.size.x,
            Math.max(
                this.options.getMinValue("schrank_breite"),
                this.options.getMinValue("element_breite") + aussenwandCount * this.options.getFloat("aussenwand_staerke")
            ),
            "minWidth",
            "minWidthText"
        );
        if (this.options.getValue("dachschraege") === "left" || this.options.getValue("dachschraege") === "right") {
            this.size.x = check("min", this.size.x, this.sizeTop.x + this.options.getMaxValue("breite_oben"), "minWidth", "minWidthText");
        }
        
        this.size.y = check("min", this.size.y, this.options.getMinValue("schrank_hoehe"), "minHeight", "minHeightText");
        this.size.y = check("min", this.size.y, this.getInterieurMinMaxBoxWithoutTueren().max.y, "minHeight", "minHeightAusstattungText");
        this.size.y = check("max", this.size.y, this.options.getMaxValue("schrank_hoehe"), "maxHeight", maxHeightI18nKey);

        if (this.options.getValue("dachschraege", null) !== null) {
            if (initiator === "setDimensionTop") {
                this.size.y = check("min", this.size.y, this.size.y - this.options.getMinValue("kniestock"), "minHeight", "minHeightText");
            } else {
                this.size.y = check("min", this.size.y, this.sizeTop.y + this.options.getMinValue("kniestock"), "minHeight", "minHeightText");
            }
        }
        // JVI: Wird nicht gemacht,weil man mit der Schrankhöhe die Elemente ändern kann
        // if (this.elements.some(e => e.locked.y)) {
        //     let minY = 0;
        //     this.elements.forEach(e => minY = Math.max(minY, e.size.y));
        //     check('min',minY, 'minHeight', 'minHeightText');
        // }

        if (this.elements.every((e) => e.locked.y)) {
            let maxY = 0;
            this.elements.forEach((e) => (maxY = Math.max(maxY, e.size.y + e.outerOffset)));
            this.size.y = maxY;
        }

        const box = new THREE.Box3();
        box.min.z = this.options.getMinValue("schrank_tiefe");
        box.max.z = this.options.getMaxValue("schrank_tiefe");

        box.union(this.getInterieurMinMaxBoxWithoutTueren());
        box.min.z = Math.max(box.min.z, this.options.getMinValue("schrank_tiefe"));
        box.max.z = Math.min(box.max.z, this.options.getMaxValue("schrank_tiefe"));

        this.size.z = check("min", this.size.z, box.min.z, "minDepth", "minDepthText");
        this.size.z = check("max", this.size.z, box.max.z, "maxDepth", "maxDepthText");

        if (this.angleSide === "left" || this.angleSide === "right") {
            this.sizeTop.x = check("min", this.sizeTop.x, this.options.getMinValue("breite_oben"), "minWidth", "minWidthText");
            this.sizeTop.x = check("max", this.sizeTop.x, this.size.x - this.options.getMaxValue("breite_oben"), "maxWidth", "maxWidthText");
        }
        if (this.angleSide === "left" || this.angleSide === "right" || this.angleSide === "back") {
            this.sizeTop.y = check(
                "min",
                this.sizeTop.y,
                this.options.getMinValue("kniestock"),
                "dialogSchrankKniestockMinHeader",
                "dialogSchrankKniestockMinText"
            );
            this.sizeTop.y = check(
                "max",
                this.sizeTop.y,
                this.size.y - this.options.getMinValue("kniestock"),
                "dialogSchrankKniestockMaxHeader",
                "dialogSchrankKniestockMaxText"
            );
        }
        if (this.angleSide === "back") {
            this.size.z = check("min", this.size.z, this.sizeTop.z, "maxDepth", "maxDepthText");
            this.sizeTop.z = check("min", this.sizeTop.z, this.options.getFloat("tiefe_oben"), "minDepth", "minDepthText");
            this.sizeTop.z = check("max", this.sizeTop.z, this.size.z - this.options.getFloat("tiefe_oben"), "maxDepth", "maxDepthText");
        }
    }

    _wandGrater(wall) {
        wall.boxToVertices();
        if (this.angleSide === "back") {
            wall.xEdgeZY(0, 1).z = wall.box.max.z - this.sizeTop.z;
            wall.xEdgeZY(2, 1)._ = { y: this.sizeTop.y, z: wall.box.min.z };
            wall.updateBox();
        } else if (this.slopings.isSloped(wall.box)) {
            wall.zEdgeXY(0, 1).y = this.slopings.yOfX(wall.box.min.x, 0);
            wall.zEdgeXY(1, 1).y = this.slopings.yOfX(wall.box.max.x, 0);
            wall.updateBox();
        }
    }
}
